import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  AccountCircle,
  Settings,
  Logout,
  Home,
  Dashboard,
  AdminPanelSettings,
  Edit,
  Visibility,
  MusicNote,
  VideoLibrary,
  Link as LinkIcon,
  Build,
  ExpandMore,
  Piano,
  AudioFile,
  PlayArrow,
  Folder,
  Image,
  YouTube,
  MovieCreation,
  Web,
  Analytics,
  BugReport,
  Search,
  PhotoAlbum,
  Tune as TemplatesIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const TopBar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // User menu state
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [mobileNavAnchorEl, setMobileNavAnchorEl] = React.useState(null);
  
  // State for navigation menu dropdowns
  const [musicMenuOpen, setMusicMenuOpen] = React.useState(false);
  const [mediaMenuOpen, setMediaMenuOpen] = React.useState(false);
  const [videoMenuOpen, setVideoMenuOpen] = React.useState(false);
  const [systemMenuOpen, setSystemMenuOpen] = React.useState(false);
  const [albumMenuOpen, setAlbumMenuOpen] = React.useState(false);
  
  // Refs for dropdown anchors
  const musicAnchorRef = React.useRef(null);
  const mediaAnchorRef = React.useRef(null);
  const videoAnchorRef = React.useRef(null);
  const systemAnchorRef = React.useRef(null);
  const albumAnchorRef = React.useRef(null);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMobileNavOpen = (event) => {
    setMobileNavAnchorEl(event.currentTarget);
  };

  const handleMobileNavClose = () => {
    setMobileNavAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    try {
      await logout();
      navigate('/auth');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleProfile = () => {
    handleClose();
    navigate('/profile');
  };

  const handleHome = () => {
    navigate('/');
  };

  const handleDashboard = () => {
    navigate('/admin/dashboard');
  };

  const getPermissionIcon = (permissionLevel) => {
    switch (permissionLevel) {
      case 'admin':
        return <AdminPanelSettings fontSize="small" />;
      case 'editor':
        return <Edit fontSize="small" />;
      case 'viewer':
        return <Visibility fontSize="small" />;
      default:
        return <AccountCircle fontSize="small" />;
    }
  };

  const getPageTitle = () => {
    const pathname = location.pathname;
    switch (pathname) {
      case '/':
        return 'Media & Music Hub';
      // Music & MIDI Section
      case '/add-midi':
        return 'Add MIDI Files';
      case '/midi-player':
        return 'MIDI Player';
      case '/midi-player-piano':
        return 'Piano Player';
      case '/midi-files':
        return 'MIDI Files';
      // Media Management Section
      case '/media-files':
        return 'Media Files';
      case '/image-upload':
        return 'Image Upload & Analysis';
      case '/view-media':
        return 'View Media';
      // Video & YouTube Section
      case '/tonality-add-youtube':
        return 'Add Tonality YouTube Videos';
      case '/tonality-youtube-video':
        return 'Display Tonality Video';
      case '/add-links':
        return 'Manage Book Links';
      // System & Diagnostics Section
      case '/cors-test':
        return 'API Connection Test';
      case '/app-insights-test':
        return 'Application Insights';
      case '/app-insights-diagnostic':
        return 'Diagnostics';
      // Admin & User Management
      case '/profile':
        return 'Profile';
      case '/admin':
        return 'Admin Panel';
      case '/admin/dashboard':
        return 'Admin Dashboard';
      case '/sessions':
        return 'My Sessions';
      case '/admin/album-templates':
        return 'Album Template Editor';
      default:
        return 'Media & Music Hub';
    }
  };

  // Navigation menu handlers
  const handleMusicMenuToggle = () => {
    setMusicMenuOpen((prevOpen) => !prevOpen);
  };

  const handleMediaMenuToggle = () => {
    setMediaMenuOpen((prevOpen) => !prevOpen);
  };

  const handleVideoMenuToggle = () => {
    setVideoMenuOpen((prevOpen) => !prevOpen);
  };

  const handleSystemMenuToggle = () => {
    setSystemMenuOpen((prevOpen) => !prevOpen);
  };

  const handleAlbumMenuToggle = () => {
    setAlbumMenuOpen((prevOpen) => !prevOpen);
  };

  const handleCloseNavMenus = (event) => {
    if (mobileNavAnchorEl && mobileNavAnchorEl.contains(event.target)) {
      return;
    }
    if (musicAnchorRef.current && musicAnchorRef.current.contains(event.target)) {
      return;
    }
    if (mediaAnchorRef.current && mediaAnchorRef.current.contains(event.target)) {
      return;
    }
    if (videoAnchorRef.current && videoAnchorRef.current.contains(event.target)) {
      return;
    }
    if (systemAnchorRef.current && systemAnchorRef.current.contains(event.target)) {
      return;
    }
    if (albumAnchorRef.current && albumAnchorRef.current.contains(event.target)) {
      return;
    }
    setMusicMenuOpen(false);
    setMediaMenuOpen(false);
    setVideoMenuOpen(false);
    setSystemMenuOpen(false);
    setAlbumMenuOpen(false);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileNavAnchorEl(null);
    setMusicMenuOpen(false);
    setMediaMenuOpen(false);
    setVideoMenuOpen(false);
    setSystemMenuOpen(false);
    setAlbumMenuOpen(false);
  };

  // Don't show top bar on auth page only
  if (location.pathname === '/auth') {
    return null;
  }

  return (
    <AppBar position="static" sx={{ mb: 2 }}>
      <Toolbar sx={{ gap: { xs: 1, md: 2 } }}>
        {/* App Title/Logo */}
        <IconButton
          size="large"
          edge="start"
          color="inherit"
          aria-label="home"
          onClick={handleHome}
          sx={{ mr: { xs: 0.5, md: 2 }, flexShrink: 0 }}
        >
          <Home />
        </IconButton>
        
        {/* Page Title */}
        <Typography
          variant="h6"
          component="div"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            fontSize: { xs: '1rem', sm: '1.15rem' },
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {getPageTitle()}
        </Typography>



        {/* Show different content based on authentication status */}
        {user ? (
          // Authenticated user content
          <>
            {/* Navigation Menu Buttons */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, mr: 2, gap: 1 }}>
              {/* Hide music/media/video/system menus for album users */}
              {user.userType !== 'album' && (
                <>
              {/* Music & MIDI Menu */}
              <Button
                ref={musicAnchorRef}
                color="inherit"
                endIcon={<ExpandMore />}
                onClick={handleMusicMenuToggle}
                sx={{ textTransform: 'none' }}
              >
                <MusicNote sx={{ mr: 0.5 }} />
                Music & MIDI
              </Button>
              
              {/* Media Management Menu */}
              <Button
                ref={mediaAnchorRef}
                color="inherit"
                endIcon={<ExpandMore />}
                onClick={handleMediaMenuToggle}
                sx={{ textTransform: 'none' }}
              >
                <Folder sx={{ mr: 0.5 }} />
                Media
              </Button>
              
              {/* Video & YouTube Menu */}
              <Button
                ref={videoAnchorRef}
                color="inherit"
                endIcon={<ExpandMore />}
                onClick={handleVideoMenuToggle}
                sx={{ textTransform: 'none' }}
              >
                <VideoLibrary sx={{ mr: 0.5 }} />
                Video & Links
              </Button>
              
              {/* System & Diagnostics Menu */}
              <Button
                ref={systemAnchorRef}
                color="inherit"
                endIcon={<ExpandMore />}
                onClick={handleSystemMenuToggle}
                sx={{ textTransform: 'none' }}
              >
                <Build sx={{ mr: 0.5 }} />
                System
              </Button>
                </>
              )}

              {/* Albums Menu */}
              <Button
                ref={albumAnchorRef}
                color="inherit"
                endIcon={<ExpandMore />}
                onClick={handleAlbumMenuToggle}
                sx={{ textTransform: 'none' }}
              >
                <PhotoAlbum sx={{ mr: 0.5 }} />
                Albums
              </Button>
              
              {/* Admin Dashboard (admin only) */}
              {user.permissionLevel === 'admin' && (
                <Button color="inherit" onClick={handleDashboard} sx={{ textTransform: 'none' }}>
                  <Dashboard sx={{ mr: 0.5 }} />
                  Admin
                </Button>
              )}
            </Box>

            <IconButton
              size="large"
              color="inherit"
              aria-label="open navigation"
              aria-controls="mobile-navigation-menu"
              aria-haspopup="true"
              onClick={handleMobileNavOpen}
              sx={{ display: { xs: 'inline-flex', md: 'none' }, flexShrink: 0 }}
            >
              <MenuIcon />
            </IconButton>

            {/* User Info and Menu */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              {/* User Info */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: { xs: 0, sm: 1 } }}>
                {getPermissionIcon(user.permissionLevel)}
                <Typography variant="body2" sx={{ display: { xs: 'none', lg: 'block' } }}>
                  {user.firstName} {user.lastName}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: { xs: 'none', md: 'block' },
                    opacity: 0.8,
                    textTransform: 'capitalize'
                  }}
                >
                  ({user.permissionLevel})
                </Typography>
              </Box>

              {/* User Menu */}
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                <Avatar 
                  src={user.profilePicture || undefined}
                  alt={`${user.firstName} ${user.lastName}`}
                  sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}
                >
                  {user.firstName[0]}{user.lastName[0]}
                </Avatar>
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                {/* User Info in Menu */}
                <MenuItem disabled>
                  <ListItemIcon>
                    {getPermissionIcon(user.permissionLevel)}
                  </ListItemIcon>
                  <ListItemText 
                    primary={`${user.firstName} ${user.lastName}`}
                    secondary={`${user.username} (${user.permissionLevel})`}
                  />
                </MenuItem>
                <Divider />

                {/* Profile */}
                <MenuItem onClick={handleProfile}>
                  <ListItemIcon>
                    <Settings fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Profile & Settings" />
                </MenuItem>

                {/* User Management (admin only) */}
                {user.permissionLevel === 'admin' && (
                  <MenuItem onClick={() => { handleClose(); navigate('/admin'); }}>
                    <ListItemIcon>
                      <AdminPanelSettings fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="User Management" />
                  </MenuItem>
                )}

                <Divider />

                {/* Logout */}
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <Logout fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Logout" />
                </MenuItem>
              </Menu>
            </Box>
          </>
        ) : (
          // Non-authenticated user content
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button 
              color="inherit" 
              variant="outlined" 
              sx={{ 
                borderColor: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
              onClick={() => navigate('/auth')}
            >
              Login
            </Button>
          </Box>
        )}
      </Toolbar>

      {user && (
        <Menu
          id="mobile-navigation-menu"
          anchorEl={mobileNavAnchorEl}
          open={Boolean(mobileNavAnchorEl)}
          onClose={handleMobileNavClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              width: 'min(92vw, 360px)',
              maxHeight: '70vh'
            }
          }}
        >
          {user.userType !== 'album' && (
            <>
              <MenuItem disabled>
                <ListItemText primary="Music & MIDI" primaryTypographyProps={{ variant: 'caption', fontWeight: 'bold' }} />
              </MenuItem>
              <MenuItem onClick={() => handleNavigation('/add-midi')}>
                <ListItemIcon><Folder fontSize="small" /></ListItemIcon>
                <ListItemText primary="Add MIDI Files" />
              </MenuItem>
              <MenuItem onClick={() => handleNavigation('/midi-player')}>
                <ListItemIcon><PlayArrow fontSize="small" /></ListItemIcon>
                <ListItemText primary="MIDI Player" />
              </MenuItem>
              <MenuItem onClick={() => handleNavigation('/midi-player-piano')}>
                <ListItemIcon><Piano fontSize="small" /></ListItemIcon>
                <ListItemText primary="Piano Player" />
              </MenuItem>
              <MenuItem onClick={() => handleNavigation('/midi-files')}>
                <ListItemIcon><AudioFile fontSize="small" /></ListItemIcon>
                <ListItemText primary="MIDI Files" />
              </MenuItem>
              <Divider />

              <MenuItem disabled>
                <ListItemText primary="Media" primaryTypographyProps={{ variant: 'caption', fontWeight: 'bold' }} />
              </MenuItem>
              <MenuItem onClick={() => handleNavigation('/media-files')}>
                <ListItemIcon><Folder fontSize="small" /></ListItemIcon>
                <ListItemText primary="Media Files" />
              </MenuItem>
              <MenuItem onClick={() => handleNavigation('/image-upload')}>
                <ListItemIcon><Image fontSize="small" /></ListItemIcon>
                <ListItemText primary="Image Upload" />
              </MenuItem>
              <MenuItem onClick={() => handleNavigation('/view-media')}>
                <ListItemIcon><Visibility fontSize="small" /></ListItemIcon>
                <ListItemText primary="View Media" />
              </MenuItem>
              <Divider />

              <MenuItem disabled>
                <ListItemText primary="Video & Links" primaryTypographyProps={{ variant: 'caption', fontWeight: 'bold' }} />
              </MenuItem>
              <MenuItem onClick={() => handleNavigation('/tonality-add-youtube')}>
                <ListItemIcon><YouTube fontSize="small" /></ListItemIcon>
                <ListItemText primary="Add Tonality Videos" />
              </MenuItem>
              <MenuItem onClick={() => handleNavigation('/tonality-youtube-video')}>
                <ListItemIcon><MovieCreation fontSize="small" /></ListItemIcon>
                <ListItemText primary="Display Tonality Video" />
              </MenuItem>
              <MenuItem onClick={() => handleNavigation('/custom-youtube-videos')}>
                <ListItemIcon><VideoLibrary fontSize="small" /></ListItemIcon>
                <ListItemText primary="Custom YouTube Player" />
              </MenuItem>
              {user?.permissionLevel === 'admin' && (
                <MenuItem onClick={() => handleNavigation('/admin/custom-youtube')}>
                  <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Manage Custom Videos" />
                </MenuItem>
              )}
              <MenuItem onClick={() => handleNavigation('/add-links')}>
                <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Manage Book Links" />
              </MenuItem>
              <Divider />

              <MenuItem disabled>
                <ListItemText primary="System" primaryTypographyProps={{ variant: 'caption', fontWeight: 'bold' }} />
              </MenuItem>
              <MenuItem onClick={() => handleNavigation('/cors-test')}>
                <ListItemIcon><Web fontSize="small" /></ListItemIcon>
                <ListItemText primary="API Connection Test" />
              </MenuItem>
              <MenuItem onClick={() => handleNavigation('/app-insights-test')}>
                <ListItemIcon><Analytics fontSize="small" /></ListItemIcon>
                <ListItemText primary="Application Insights" />
              </MenuItem>
              <MenuItem onClick={() => handleNavigation('/log-analysis')}>
                <ListItemIcon><Search fontSize="small" /></ListItemIcon>
                <ListItemText primary="AI Log Analysis" />
              </MenuItem>
              <MenuItem onClick={() => handleNavigation('/app-insights-diagnostic')}>
                <ListItemIcon><BugReport fontSize="small" /></ListItemIcon>
                <ListItemText primary="Diagnostics" />
              </MenuItem>
              <Divider />
            </>
          )}

          <MenuItem disabled>
            <ListItemText primary="Albums" primaryTypographyProps={{ variant: 'caption', fontWeight: 'bold' }} />
          </MenuItem>
          <MenuItem onClick={() => handleNavigation('/sessions')}>
            <ListItemIcon><PhotoAlbum fontSize="small" /></ListItemIcon>
            <ListItemText primary="My Sessions" />
          </MenuItem>
          {user?.permissionLevel === 'admin' && (
            <MenuItem onClick={() => handleNavigation('/admin/album-templates')}>
              <ListItemIcon><TemplatesIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Album Templates" />
            </MenuItem>
          )}
          {user.permissionLevel === 'admin' && (
            <MenuItem onClick={() => handleNavigation('/admin/dashboard')}>
              <ListItemIcon><Dashboard fontSize="small" /></ListItemIcon>
              <ListItemText primary="Admin Dashboard" />
            </MenuItem>
          )}
        </Menu>
      )}
      
      {/* Dropdown Menus - Rendered outside Toolbar for proper positioning */}
      {user && (
        <>
          {/* Music & MIDI Dropdown */}
          <Menu
            anchorEl={musicAnchorRef.current}
            open={musicMenuOpen}
            onClose={handleCloseNavMenus}
            MenuListProps={{
              'aria-labelledby': 'music-button',
            }}
          >
            <MenuItem onClick={() => handleNavigation('/add-midi')}>
              <ListItemIcon>
                <Folder fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Add MIDI Files"
                secondary="Upload and organize your MIDI compositions"
              />
            </MenuItem>
            <MenuItem onClick={() => handleNavigation('/midi-player')}>
              <ListItemIcon>
                <PlayArrow fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="MIDI Player"
                secondary="Play MIDI files with customizable instruments"
              />
            </MenuItem>
            <MenuItem onClick={() => handleNavigation('/midi-player-piano')}>
              <ListItemIcon>
                <Piano fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Piano Player"
                secondary="Interactive piano visualization for MIDI playback"
              />
            </MenuItem>
            <MenuItem onClick={() => handleNavigation('/midi-files')}>
              <ListItemIcon>
                <AudioFile fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="MIDI Files"
                secondary="Browse your MIDI file collection"
              />
            </MenuItem>
          </Menu>

          {/* Media Management Dropdown */}
          <Menu
            anchorEl={mediaAnchorRef.current}
            open={mediaMenuOpen}
            onClose={handleCloseNavMenus}
            MenuListProps={{
              'aria-labelledby': 'media-button',
            }}
          >
            <MenuItem onClick={() => handleNavigation('/media-files')}>
              <ListItemIcon>
                <Folder fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Media Files"
                secondary="Upload and manage your media collection"
              />
            </MenuItem>
            <MenuItem onClick={() => handleNavigation('/image-upload')}>
              <ListItemIcon>
                <Image fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Image Upload"
                secondary="Upload images with EXIF and GPS data analysis"
              />
            </MenuItem>
            <MenuItem onClick={() => handleNavigation('/view-media')}>
              <ListItemIcon>
                <Visibility fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="View Media"
                secondary="Browse and preview your uploaded media"
              />
            </MenuItem>
          </Menu>

          {/* Video & Links Dropdown */}
          <Menu
            anchorEl={videoAnchorRef.current}
            open={videoMenuOpen}
            onClose={handleCloseNavMenus}
            MenuListProps={{
              'aria-labelledby': 'video-button',
            }}
          >
            <MenuItem disabled>
              <ListItemText 
                primary="Tonality YouTube Videos"
                primaryTypographyProps={{ variant: 'caption', fontWeight: 'bold', color: 'primary' }}
              />
            </MenuItem>
            <MenuItem onClick={() => handleNavigation('/tonality-add-youtube')}>
              <ListItemIcon>
                <YouTube fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Add Tonality YouTube Videos"
                secondary="Add YouTube videos with tonality analysis"
              />
            </MenuItem>
            <MenuItem onClick={() => handleNavigation('/tonality-youtube-video')}>
              <ListItemIcon>
                <MovieCreation fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Display Tonality Video"
                secondary="Watch videos with tonality visualization"
              />
            </MenuItem>
            <Divider sx={{ my: 1 }} />
            <MenuItem disabled>
              <ListItemText 
                primary="Custom YouTube Videos"
                primaryTypographyProps={{ variant: 'caption', fontWeight: 'bold', color: 'success.main' }}
              />
            </MenuItem>
            <MenuItem onClick={() => handleNavigation('/custom-youtube-videos')}>
              <ListItemIcon>
                <VideoLibrary fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Custom YouTube Player"
                secondary="Watch videos with custom playback controls"
              />
            </MenuItem>
            {user?.permissionLevel === 'admin' && (
              <MenuItem onClick={() => handleNavigation('/admin/custom-youtube')}>
                <ListItemIcon>
                  <Settings fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Manage Custom Videos"
                  secondary="Admin panel for custom YouTube videos with QR codes"
                />
              </MenuItem>
            )}
            <Divider sx={{ my: 1 }} />
            <MenuItem disabled>
              <ListItemText 
                primary="Book Links"
                primaryTypographyProps={{ variant: 'caption', fontWeight: 'bold', color: 'warning.main' }}
              />
            </MenuItem>
            <MenuItem onClick={() => handleNavigation('/add-links')}>
              <ListItemIcon>
                <LinkIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Manage Book Links"
                secondary="Organize and categorize your book links"
              />
            </MenuItem>
          </Menu>

          {/* System & Diagnostics Dropdown */}
          <Menu
            anchorEl={systemAnchorRef.current}
            open={systemMenuOpen}
            onClose={handleCloseNavMenus}
            MenuListProps={{
              'aria-labelledby': 'system-button',
            }}
          >
            <MenuItem onClick={() => handleNavigation('/cors-test')}>
              <ListItemIcon>
                <Web fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="API Connection Test"
                secondary="Test backend connectivity and CORS settings"
              />
            </MenuItem>
            <MenuItem onClick={() => handleNavigation('/app-insights-test')}>
              <ListItemIcon>
                <Analytics fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Application Insights"
                secondary="Monitor application performance and usage"
              />
            </MenuItem>
            <MenuItem onClick={() => handleNavigation('/log-analysis')}>
              <ListItemIcon>
                <Search fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="AI Log Analysis"
                secondary="Analyze logs for anomalies and insights"
              />
            </MenuItem>
            <MenuItem onClick={() => handleNavigation('/app-insights-diagnostic')}>
              <ListItemIcon>
                <BugReport fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Diagnostics"
                secondary="Advanced debugging and troubleshooting tools"
              />
            </MenuItem>
          </Menu>

          {/* Albums Dropdown */}
          <Menu
            anchorEl={albumAnchorRef.current}
            open={albumMenuOpen}
            onClose={handleCloseNavMenus}
            MenuListProps={{ 'aria-labelledby': 'albums-button' }}
          >
            <MenuItem onClick={() => handleNavigation('/sessions')}>
              <ListItemIcon>
                <PhotoAlbum fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="My Sessions"
                secondary="View and manage your photo album sessions"
              />
            </MenuItem>
            {user?.permissionLevel === 'admin' && (
              <>
                <Divider sx={{ my: 1 }} />
                <MenuItem onClick={() => handleNavigation('/admin/album-templates')}>
                  <ListItemIcon>
                    <TemplatesIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Album Templates"
                    secondary="Manage page layout templates for the album wizard"
                  />
                </MenuItem>
              </>
            )}
          </Menu>
        </>
      )}
    </AppBar>
  );
};

export default TopBar;
